/**
 * Hooks for fetching reward templates
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { rewardsApi } from '@api/endpoints/rewards'
import type {
  CreateGiftRewardTemplateRequest,
  UpdateGiftRewardTemplateRequest,
  DistributeRewardRequest,
} from '@api/types/rewards'

/**
 * Fetch templates for a group
 */
export function useRewardTemplates(groupId: string | undefined, page: number = 1) {
  return useQuery({
    queryKey: ['reward-templates', groupId, page],
    queryFn: () => rewardsApi.getGroupTemplates(groupId!, page),
    enabled: !!groupId,
  })
}

/**
 * Fetch a single template
 */
export function useRewardTemplate(groupId: string | undefined, templateId: string | undefined) {
  return useQuery({
    queryKey: ['reward-template', groupId, templateId],
    queryFn: () => rewardsApi.getTemplate(groupId!, templateId!),
    enabled: !!groupId && !!templateId,
  })
}

/**
 * Fetch template stats
 */
export function useTemplateStats(groupId: string | undefined, templateId: string | undefined) {
  return useQuery({
    queryKey: ['template-stats', groupId, templateId],
    queryFn: () => rewardsApi.getTemplateStats(groupId!, templateId!),
    enabled: !!groupId && !!templateId,
  })
}

/**
 * Fetch template redemption history
 */
export function useRedemptionHistory(groupId: string | undefined, templateId: string | undefined, page: number = 1) {
  return useQuery({
    queryKey: ['redemption-history', groupId, templateId, page],
    queryFn: () => rewardsApi.getRedemptionHistory(groupId!, templateId!, page),
    enabled: !!groupId && !!templateId,
  })
}

/**
 * Fetch template transfer history
 */
export function useTransferHistory(groupId: string | undefined, templateId: string | undefined, page: number = 1) {
  return useQuery({
    queryKey: ['transfer-history', groupId, templateId, page],
    queryFn: () => rewardsApi.getTransferHistory(groupId!, templateId!, page),
    enabled: !!groupId && !!templateId,
  })
}

/**
 * Create a new template
 */
export function useCreateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: CreateGiftRewardTemplateRequest }) =>
      rewardsApi.createTemplate(groupId, data),
    onSuccess: (_, variables) => {
      // Invalidate templates list for the group
      queryClient.invalidateQueries({ queryKey: ['reward-templates', variables.groupId] })
    },
  })
}

/**
 * Update a template
 */
export function useUpdateTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      groupId,
      templateId,
      data,
    }: {
      groupId: string
      templateId: string
      data: UpdateGiftRewardTemplateRequest
    }) => rewardsApi.updateTemplate(groupId, templateId, data),
    onSuccess: (data, variables) => {
      // Invalidate the specific template
      queryClient.invalidateQueries({ queryKey: ['reward-template', variables.groupId, data.id] })
      // Invalidate templates list for the group
      queryClient.invalidateQueries({ queryKey: ['reward-templates', variables.groupId] })
    },
  })
}

/**
 * Delete a template
 */
export function useDeleteTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, templateId }: { groupId: string; templateId: string }) =>
      rewardsApi.deleteTemplate(groupId, templateId),
    onSuccess: (_, variables) => {
      // Invalidate templates list for the group
      queryClient.invalidateQueries({ queryKey: ['reward-templates', variables.groupId] })
    },
  })
}

/**
 * Toggle template active status
 */
export function useToggleTemplateActive() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ groupId, templateId }: { groupId: string; templateId: string }) =>
      rewardsApi.toggleTemplateActive(groupId, templateId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['reward-template', variables.groupId, data.id] })
      queryClient.invalidateQueries({ queryKey: ['reward-templates', variables.groupId] })
    },
  })
}

/**
 * Distribute rewards
 */
export function useDistributeRewards() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      groupId,
      templateId,
      data,
    }: {
      groupId: string
      templateId: string
      data: DistributeRewardRequest
    }) => rewardsApi.distributeRewards(groupId, templateId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['template-stats', variables.groupId, variables.templateId] })
      queryClient.invalidateQueries({ queryKey: ['reward-template', variables.groupId, variables.templateId] })
    },
  })
}
